import React from "react";
import type { CampaignReportResponse } from "../../types";
import type { ExportTemplateModule } from "../types";
import { extractTrendSeries, formatCurrency } from "../utils";

interface TrendLineModuleProps {
  data: CampaignReportResponse;
  module: ExportTemplateModule<"trend-line">;
}

export const TrendLineModule: React.FC<TrendLineModuleProps> = ({
  data,
  module,
}) => {
  const metric = module.props?.metric || "total_sales";
  const series = extractTrendSeries(data, metric);
  const points = series.points;

  if (points.length < 2) {
    return (
      <section>
        {module.title && (
          <div className="text-slate-400 uppercase tracking-[0.2em] text-xs font-semibold mb-3">
            {module.title}
          </div>
        )}
        <div className="rounded-2xl bg-white border border-slate-100 shadow-[0_20px_45px_rgba(15,23,42,0.08)] p-6 text-slate-500 text-sm">
          Not enough daily data to render this visualization.
        </div>
      </section>
    );
  }

  const width = 760;
  const height = 220;
  const values = points.map((p) => p.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;

  const pathData = points
    .map((point, index) => {
      const x = (index / (points.length - 1)) * width;
      const normalized = (point.value - min) / range;
      const y = height - normalized * height;
      return `${index === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(" ");

  const areaData = `${pathData} L${width},${height} L0,${height} Z`;

  const latest = points[points.length - 1];
  const first = points[0];
  const delta = latest.value - first.value;
  const deltaPercent = (delta / (first.value || 1)) * 100;

  return (
    <section>
      {module.title && (
        <div className="flex items-center justify-between mb-3">
          <div className="text-slate-400 uppercase tracking-[0.2em] text-xs font-semibold">
            {module.title}
          </div>
          <div className="text-xs text-slate-500">
            {heroDateRange(data)}
          </div>
        </div>
      )}
      <div className="rounded-2xl bg-white border border-slate-100 shadow-[0_25px_50px_rgba(15,23,42,0.08)] p-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-sm text-slate-500 uppercase tracking-wide">
              Latest {metric.replace("total_", "").replace("_", " ")}
            </div>
            <div className="text-3xl font-semibold text-slate-900">
              {formatCurrency(latest.value, { maximumFractionDigits: 0 })}
            </div>
          </div>
          <div
            className={`text-right ${
              delta >= 0 ? "text-emerald-500" : "text-rose-500"
            }`}
          >
            <div className="text-lg font-semibold">
              {delta >= 0 ? "+" : ""}
              {formatCurrency(delta, { maximumFractionDigits: 0 })}
            </div>
            <div className="text-sm">
              {deltaPercent >= 0 ? "+" : ""}
              {deltaPercent.toFixed(1)}% vs. period start
            </div>
          </div>
        </div>
        <div className="overflow-hidden rounded-2xl bg-gradient-to-br from-slate-50 to-violet-50">
          <svg
            viewBox={`0 0 ${width} ${height}`}
            width="100%"
            height="240"
            preserveAspectRatio="none"
          >
            <defs>
              <linearGradient id="trendAreaGradient" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="rgba(59,130,246,0.25)" />
                <stop offset="100%" stopColor="rgba(59,130,246,0)" />
              </linearGradient>
              <linearGradient id="trendLineGradient" x1="0" x2="1" y1="0" y2="0">
                <stop offset="0%" stopColor="#38bdf8" />
                <stop offset="100%" stopColor="#ec4899" />
              </linearGradient>
            </defs>
            <path d={areaData} fill="url(#trendAreaGradient)" />
            <path
              d={pathData}
              fill="none"
              stroke="url(#trendLineGradient)"
              strokeWidth={4}
              strokeLinecap="round"
            />
          </svg>
        </div>
      </div>
    </section>
  );
};

function heroDateRange(data: CampaignReportResponse): string {
  return `${data.summary.date_range_start} → ${data.summary.date_range_end}`;
}
